import { Stack, Typography } from "@mui/material";
import { TextInput } from "components/TextInput";
import { isValidSkillSlug, slugifySkillDisplayName } from "encamp-shared/src/skills/slug";
import { Control, Controller, useFormState, useWatch } from "react-hook-form";

import type { SkillEditorFormValues } from "./SkillEditor";

/** The server rejects an invalid slug too; this says so before saving. */
function validateSkillSlug(displayName: string): true | string {
  return isValidSkillSlug(slugifySkillDisplayName(displayName))
    ? true
    : "This name doesn't make a usable /slug";
}

/** The skill's display name as the dialog title, edited in place like a task title. */
export function SkillTitleInput({
  control,
  readOnly,
  autoFocus = false,
}: {
  control: Control<SkillEditorFormValues>;
  readOnly: boolean;
  autoFocus?: boolean;
}) {
  const displayName = useWatch({ control, name: "displayName" }) ?? "";
  const slug = slugifySkillDisplayName(displayName);
  const { errors } = useFormState({ control, name: "displayName" });
  const slugError = errors.displayName?.type === "validate" ? errors.displayName.message : null;

  return (
    <Stack flex={1} sx={{ minWidth: 0, pr: 1 }}>
      <Controller
        name="displayName"
        control={control}
        rules={{ required: "Display name is required", validate: validateSkillSlug }}
        render={({ field, fieldState: { error } }) => (
          <Stack gap={1} direction="row" sx={{ minWidth: 0 }}>
            {!!error && (
              <Typography variant="caption" color="error">
                *
              </Typography>
            )}
            <TextInput
              autoFocus={autoFocus}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              readOnly={readOnly}
              variant="title"
              placeholder="Skill name"
              inputProps={{ "aria-label": "Skill name" }}
            />
          </Stack>
        )}
      />
      <Typography
        variant="caption"
        color={slugError ? "error" : "text.secondary"}
        noWrap
        data-testid="skill-title-slug"
        aria-label="Skill slug"
      >
        /{slug}
        {slugError ? ` · ${slugError}` : null}
      </Typography>
    </Stack>
  );
}
